import React, { useState, useEffect } from 'react';
import {
  string,
  bool,
  shape,
  arrayOf,
  oneOf
} from 'prop-types';
import { dataModel } from './dataModel';
import { FuelTypeProvider, FuelTypeAlert, FuelTypeDrawer } from './components';
import { ENTRY_TYPES, IMAGES } from './constants';

export const ApplianceFuelType = ({
  itemId,
  entryType,
  position,
  product,
  metadata,
  search,
  imageCacheEnabled
}) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('appliance-fuel-type.ready'); }, []);

  const [isCaching, setIsCaching] = useState(imageCacheEnabled && typeof window !== 'undefined');

  /* istanbul ignore next */
  useEffect(() => {
    const cacheImages = async () => {
      try {
        if (!ENTRY_TYPES.includes(entryType)) throw new Error('No entryType provided to ApplianceFuelType');
        await Promise.all(Object.values(IMAGES).map(async (src) => {
          return new Promise((resolve, reject) => {
            const image = new Image();
            image.src = src;
            image.onload = resolve;
            image.onerror = reject;
          });
        }));
        setIsCaching(false);
      } catch (er) {
        console.log(er);
        setIsCaching(false);
      }
    };
    if (imageCacheEnabled) cacheImages();
  }, [imageCacheEnabled, entryType]);

  if (!itemId && !product && !search) return null;

  return (
    <div
      data-testid="appliance-fuel-type"
      data-component="ApplianceFuelType"
    >
      <FuelTypeProvider
        itemId={itemId}
        entryType={entryType}
        position={position}
        isCaching={isCaching}
        product={product}
        metadata={metadata}
        search={search}
      >
        <FuelTypeAlert />
        <FuelTypeDrawer />
      </FuelTypeProvider>
    </div>
  );
};

ApplianceFuelType.propTypes = {
  itemId: string,
  entryType: oneOf(ENTRY_TYPES),
  position: oneOf(['left', 'right']),
  imageCacheEnabled: bool,
  product: shape({
    itemId: string,
    dataSource: string,
    identifiers: shape({
      isSuperSku: bool,
      parentId: string
    }),
    specificationGroup: arrayOf(shape({
      specTitle: string,
      specifications: arrayOf(shape({
        specName: string,
        specValue: string
      }))
    }))
  }),
  metadata: shape({
    attributes: arrayOf(shape({
      attributeValues: arrayOf(shape({
        value: string
      })),
      attributeName: string
    })),
    superDuperSku: shape({
      attributes: arrayOf(shape({
        attributeName: string,
        attributeValues: arrayOf(shape({
          selected: bool,
          value: string
        }))
      }))
    })
  }),
  search: shape({
    dimensions: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    })),
    primaryFilters: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    }))
  })
};

ApplianceFuelType.defaultProps = {
  itemId: '',
  entryType: '',
  position: 'right',
  imageCacheEnabled: false,
  product: null,
  metadata: null,
  search: null
};

ApplianceFuelType.dataModel = dataModel;

ApplianceFuelType.displayName = 'ApplianceFuelType';
